import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader, StatusBadge } from "@/components/common";
import { api } from "@/lib/api";
import { ago } from "@/lib/format";
import { label } from "@/lib/labels";

interface ReportRow {
  id: string;
  targetType: string;
  targetId: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
  review: { rating: number | null; comment: string | null; status: string } | null;
}

const TARGET: Record<string, string> = {
  review: "Yorum",
  message: "Mesaj",
  profile: "Profil",
  portfolio: "Portföy",
  blog_comment: "Blog yorumu",
};

function DecideReport({ r }: { r: ReportRow }) {
  const qc = useQueryClient();
  const [decision, setDecision] = React.useState<"uphold" | "reject">("uphold");
  const [note, setNote] = React.useState("");
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant="outline">
          Karar ver
        </Button>
      }
      title="Bildirim kararı"
      description="Gerekçe bildirene ve içeriğin sahibine gönderilir (DSA, K-083). Aynı içerikteki diğer açık bildirimler de kapanır."
      confirmLabel="Kaydet"
      canSubmit={note.trim().length >= 3}
      success="Karar kaydedildi."
      onSubmit={async (key) => {
        const res = await api<{ manualActionNeeded: boolean }>(`/v1/admin/reports/${r.id}/decide`, {
          method: "POST",
          idempotencyKey: key,
          body: { decision, note: note.trim() },
        });
        if (res.manualActionNeeded) alertManual();
        await qc.invalidateQueries({ queryKey: ["admin-reports"] });
      }}
    >
      <div className="grid gap-3">
        {r.review && (
          <blockquote className="rounded-md border-l-2 border-primary bg-muted/50 px-3 py-2 text-sm">
            <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
              <Star className="size-3" /> {r.review.rating ?? "—"}
            </div>
            {r.review.comment ?? <i className="text-muted-foreground">metin yok</i>}
          </blockquote>
        )}
        {r.details && <p className="text-sm text-muted-foreground">Bildiren: “{r.details}”</p>}
        <div className="grid gap-2">
          <Label htmlFor={`d-${r.id}`}>Karar</Label>
          <NativeSelect
            id={`d-${r.id}`}
            value={decision}
            onChange={(e) => setDecision(e.target.value as "uphold" | "reject")}
          >
            <option value="uphold">Haklı — içerik kaldırılır</option>
            <option value="reject">Haksız — içerik kalır</option>
          </NativeSelect>
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`n-${r.id}`}>Gerekçe</Label>
          <Textarea
            id={`n-${r.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
          />
        </div>
      </div>
    </ActionDialog>
  );
}

/** yorum/mesaj dışındaki içerikte (profil, portföy) kaldırma elle yapılır */
function alertManual() {
  toast.warning("Bu içerik türü otomatik kaldırılmıyor; ilgili kaydı elle düzenleyin.");
}

export function ReportsPage() {
  const [status, setStatus] = React.useState("open");
  const q = useQuery({
    queryKey: ["admin-reports", status],
    queryFn: () => api<{ reports: ReportRow[] }>(`/v1/admin/reports?status=${status}&limit=100`),
  });
  const columns: ColumnDef<ReportRow, unknown>[] = [
    {
      accessorKey: "targetType",
      header: "İçerik",
      cell: ({ row }) => TARGET[row.original.targetType] ?? row.original.targetType,
    },
    { accessorKey: "reason", header: "Neden", cell: ({ row }) => label(row.original.reason) },
    {
      id: "preview",
      header: "Önizleme",
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-md text-muted-foreground">
          {row.original.review?.comment ?? row.original.details ?? "—"}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Durum",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "createdAt",
      header: "Bildirildi",
      cell: ({ row }) => ago(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.status === "open" ? (
          <div className="flex justify-end">
            <DecideReport r={row.original} />
          </div>
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title="İçerik bildirimleri"
        description="Yorum, mesaj ve profil şikâyetleri (K-076, K-083)."
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="open">Açık</option>
            <option value="upheld">Kabul edilen</option>
            <option value="rejected">Reddedilen</option>
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.reports}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Açık bildirim yok."
      />
    </>
  );
}

interface FlaggedMessage {
  id: string;
  conversationId: string;
  taskId: string | null;
  senderId: string | null;
  body: string;
  flagReason: string | null;
  flaggedAt: string;
  createdAt: string;
}

function ModerateMessage({ m, action }: { m: FlaggedMessage; action: "clear" | "remove" }) {
  const qc = useQueryClient();
  const [note, setNote] = React.useState("");
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant={action === "remove" ? "outline" : "ghost"}>
          {action === "remove" ? "Metni kaldır" : "Sorun yok"}
        </Button>
      }
      title={action === "remove" ? "Mesaj metnini kaldır" : "İşareti kaldır"}
      description={
        action === "remove"
          ? "Mesaj sohbette “kaldırıldı” olarak görünür."
          : "Mesaj olduğu gibi kalır."
      }
      confirmLabel={action === "remove" ? "Kaldır" : "Onayla"}
      confirmVariant={action === "remove" ? "destructive" : "default"}
      canSubmit={note.trim().length >= 3}
      success="Kaydedildi."
      onSubmit={async (key) => {
        await api(`/v1/admin/messages/${m.id}/moderate`, {
          method: "POST",
          idempotencyKey: key,
          body: { action, note: note.trim() },
        });
        await qc.invalidateQueries({ queryKey: ["admin-messages"] });
      }}
    >
      <div className="grid gap-2">
        <Label htmlFor={`m-${m.id}-${action}`}>Not (denetim kaydı)</Label>
        <Textarea
          id={`m-${m.id}-${action}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
        />
      </div>
    </ActionDialog>
  );
}

export function MessagesPage() {
  const q = useQuery({
    queryKey: ["admin-messages"],
    queryFn: () => api<{ messages: FlaggedMessage[] }>("/v1/admin/messages/flagged"),
  });
  const columns: ColumnDef<FlaggedMessage, unknown>[] = [
    {
      accessorKey: "body",
      header: "Mesaj",
      cell: ({ row }) => (
        <span className="line-clamp-3 max-w-lg whitespace-pre-line">{row.original.body}</span>
      ),
    },
    {
      accessorKey: "flagReason",
      header: "Neden",
      cell: ({ row }) => label(row.original.flagReason),
    },
    {
      accessorKey: "flaggedAt",
      header: "İşaretlendi",
      cell: ({ row }) => ago(row.original.flaggedAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <ModerateMessage m={row.original} action="clear" />
          <ModerateMessage m={row.original} action="remove" />
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="İşaretli mesajlar"
        description="Sohbette kullanıcıların şikâyet ettiği mesajlar (K-044)."
      />
      <DataTable
        columns={columns}
        data={q.data?.messages}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="İşaretli mesaj yok."
      />
    </>
  );
}
